"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.projectAppBaseBindingDatasets = projectAppBaseBindingDatasets;
/** Explicit adapters for the two canonical Bridge response contracts. */
function projectAppBaseBindingDatasets(input, sourceKeys, mode) {
    const record = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
    const fail = () => {
        throw new Error('Invalid App binding dataset projection');
    };
    if (!record(input) || sourceKeys.length > 32)
        return fail();
    let rows = 0;
    const result = Object.create(null);
    for (const sourceKey of sourceKeys) {
        if (!Object.prototype.hasOwnProperty.call(input, sourceKey))
            return fail();
        const dataset = input[sourceKey];
        if (!record(dataset))
            return fail();
        if (dataset.status === 'unavailable') {
            result[sourceKey] = {
                status: 'unavailable',
                columns: [],
                rows: [],
                ...(typeof dataset.reason === 'string'
                    ? { reason: dataset.reason }
                    : {}),
            };
            continue;
        }
        if (dataset.status !== 'available' ||
            !Array.isArray(dataset.columns) ||
            !Array.isArray(dataset.rows))
            return fail();
        if (dataset.literalRows !== undefined &&
            !Array.isArray(dataset.literalRows))
            return fail();
        rows +=
            dataset.rows.length +
                (Array.isArray(dataset.literalRows) ? dataset.literalRows.length : 0);
        if (rows > 5000)
            return fail();
        const columns = dataset.columns.map(column => {
            if (!record(column) || typeof column.id !== 'string')
                return fail();
            if (column.options === undefined)
                return { id: column.id };
            // A select column brings its option labels: its cells hold their IDs.
            if (!Array.isArray(column.options))
                return fail();
            return {
                id: column.id,
                options: column.options.map(option => record(option) &&
                    typeof option.id === 'string' &&
                    typeof option.value === 'string'
                    ? { id: option.id, value: option.value }
                    : fail()),
            };
        });
        const allowed = new Set(columns.map(column => column.id));
        const projectRows = (input) => {
            if (!Array.isArray(input))
                return fail();
            return input.map(row => {
                if (!record(row) || typeof row.id !== 'string' || !record(row.cells))
                    return fail();
                const cells = Object.create(null);
                for (const [columnId, value] of Object.entries(row.cells)) {
                    // A row read in another language carries its language state next
                    // to the columns. No binding names it. The keys stand here as text
                    // because this function is embedded by its source.
                    if (columnId === '__contentI18n' || columnId === '__i18n')
                        continue;
                    if (!allowed.has(columnId))
                        return fail();
                    if (mode === 'internal_live') {
                        if (!record(value) ||
                            !Object.prototype.hasOwnProperty.call(value, 'value'))
                            return fail();
                        cells[columnId] = value.value;
                    }
                    else
                        cells[columnId] = value;
                }
                return { id: row.id, cells };
            });
        };
        result[sourceKey] = {
            status: 'available',
            columns,
            ...(dataset.counts === undefined
                ? {}
                : {
                    counts: (() => {
                        if (!record(dataset.counts) ||
                            Object.keys(dataset.counts).length > 32)
                            return fail();
                        const counts = Object.create(null);
                        for (const [key, value] of Object.entries(dataset.counts)) {
                            if (typeof value !== 'number' ||
                                !Number.isSafeInteger(value) ||
                                value < 0)
                                return fail();
                            counts[key] = value;
                        }
                        return counts;
                    })(),
                }),
            rows: projectRows(dataset.rows),
            // The window of a listed source: where it begins (absent: at its
            // first row) and of how many rows the list is.
            ...['offset', 'totalCount'].reduce((window, field) => {
                const value = dataset[field];
                if (value === undefined)
                    return window;
                if (typeof value !== 'number' ||
                    !Number.isSafeInteger(value) ||
                    value < 0)
                    return fail();
                window[field] = value;
                return window;
            }, {}),
            ...(dataset.literalRows === undefined
                ? {}
                : { literalRows: projectRows(dataset.literalRows) }),
            // Literal-key lists that read their own windows name them.
            ...(dataset.literalWindows === undefined
                ? {}
                : {
                    literalWindows: (() => {
                        const windows = dataset.literalWindows;
                        if (!record(windows) || Object.keys(windows).length > 64)
                            return fail();
                        const result = Object.create(null);
                        for (const [key, window] of Object.entries(windows)) {
                            if (!record(window) ||
                                typeof window.offset !== 'number' ||
                                typeof window.totalCount !== 'number' ||
                                !Number.isSafeInteger(window.offset) ||
                                !Number.isSafeInteger(window.totalCount) ||
                                window.offset < 0 ||
                                window.totalCount < 0 ||
                                !Array.isArray(window.rowIds) ||
                                window.rowIds.some(id => typeof id !== 'string'))
                                return fail();
                            result[key] = {
                                offset: window.offset,
                                totalCount: window.totalCount,
                                rowIds: window.rowIds,
                            };
                        }
                        return result;
                    })(),
                }),
        };
    }
    if (new TextEncoder().encode(JSON.stringify(result)).length > 2 * 1024 * 1024)
        return fail();
    return result;
}
